import {
  Box,
  Typography,
  Avatar,
  Fade,
  Button,
  TextField,
  Container,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useMutation } from "react-query";
import * as Yup from "yup";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import FTextField from "../../components/Formik/FTextField";
import FSelect from "../../components/Formik/FSelect";
import FMultiSelect from "../../components/Formik/FMultiSelect";
import {
  FormikStep,
  FormikStepper,
} from "../../containers/Formulaire/FormikStepper";
import FDatePicker from "../../components/Formik/FDatePicker";
import axios from "axios";
import { CustomAlert } from "../../components/CustomAlert";
import { useState } from "react";
import FFieldArray from "../../components/Formik/FFieldArray";
import FFieldArray2 from "../../components/Formik/FFieldArray2";

const { REACT_APP_PATH } = process.env;

export const PostFunction = async (file) => {
  return axios.post(`${REACT_APP_PATH}/sendmail/`, file, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

const useSendMail = (setAlert) => {
  return useMutation((file) => PostFunction(file), {
    onSuccess: (data) => {
      setAlert({
        open: true,
        type: "success",
        text: "Les données sont envoyées avec succès",
      });
    },
    onError: (error) => {
      setAlert({
        open: true,
        type: "error",
        text: "Error lors de l'envoie des données, réessayez plus tard",
      });
    },
  });
};

const AfriqualeosForm = () => {
  const [alert, setAlert] = useState({ open: false, text: "" });

  const FORM_VALIDATION1 = Yup.object().shape({
    name: Yup.string().required("Le nom de l'entreprise est requis"),
    adresse: Yup.string().required("L'adresse de l'entreprise est requis"),
    num_cnss: Yup.string(),
    ICE: Yup.number().typeError("ICE doit être un nombre"),
    contact_name: Yup.string(),

    contact_email: Yup.string()
      .email("Cette adresse mail est invalid")
      .required("L'adresse mail requis"),
    contact_tel: Yup.number().typeError(
      "Le numéro du telephone doit être un nombre"
    ),
  });

  const FORM_VALIDATION2 = Yup.object().shape({
    activities_products: Yup.array(
      Yup.string().required("Le site est requis")
    ).min(
      1,
      "Les activités/produits sont requis, vous devez avoir au moins une activité/produit"
    ),

    sites: Yup.array(
      Yup.object({
        name: Yup.string().required("Le site est requis"),
        effectif: Yup.string().required("L'effectif est requis"),
      })
    ).min(1, "Les sites sont requis, vous devez avoir au moins un site"),

    has_cartographie: Yup.string().required("Ce champ est requis"),
    fournir_cartographie: Yup.mixed().when("has_cartographie", {
      is: "Oui",
      then: Yup.string().required("Ce champ est requis"),
      otherwise: Yup.string(),
    }),

    has_organigramme: Yup.string().required("Ce champ est requis"),
    fournir_organigramme: Yup.mixed().when("has_organigramme", {
      is: "Oui",
      then: Yup.string().required("Ce champ est requis"),
      otherwise: Yup.string(),
    }),

    has_procedures: Yup.string().required("Ce champ est requis"),
    number_procedures: Yup.mixed().when("has_procedures", {
      is: "Oui",
      then: Yup.number().required("Ce champ est requis"),
      otherwise: Yup.number(),
    }),

    legale_reglementaire: Yup.string().required("Ce champ est requis"),
  });

  const FORM_VALIDATION3 = Yup.object().shape({
    demarche: Yup.array().min(1, "La demarche est requis"),
    other_demarches: Yup.mixed().when("demarche", {
      is: (demarche) => {
        if (demarche.includes("Autres")) return true;
      },
      then: Yup.string().required("Ce champ est requis"),
      otherwise: Yup.string(),
    }),

    intern_charge: Yup.string().required("Ce champ est requis"),

    start_date: Yup.date()
      .required("La date de début est requis")
      .typeError("La date choisie est invalid"),
    certification_date: Yup.date()
      .required("La date de certification est requis")
      .typeError("La date choisie est invalid"),

    financement: Yup.string().required("Ce champ est requis"),
    financement_organisme: Yup.mixed().when("financement", {
      is: "Oui",
      then: Yup.string().required("Ce champ est requis"),
      otherwise: Yup.string(),
    }),

    has_certification_organisme: Yup.string().required("Ce champ est requis"),
    certification_organisme: Yup.mixed().when("has_certification_organisme", {
      is: "Oui",
      then: Yup.string().required("Ce champ est requis"),
      otherwise: Yup.string(),
    }),
  });

  const INITIAL_FORM_STATE = {
    name: "",
    adresse: "",
    num_cnss: "",
    ICE: "",
    contact_name: "",
    contact_email: "",
    contact_tel: "",

    activities_products: [""],
    sites: [{ name: "", effectif: "" }],

    has_cartographie: "",
    fournir_cartographie: "",

    has_organigramme: "",
    fournir_organigramme: "",

    has_procedures: "",
    number_procedures: "",

    legale_reglementaire: "",

    demarche: [],
    other_demarches: "",

    intern_charge: "",

    start_date: null,
    certification_date: null,

    financement: "",
    financement_organisme: "",

    has_certification_organisme: "",
    certification_organisme: "",
  };

  const { isLoading, mutate } = useSendMail(setAlert);

  const handleSubmit = async (data) => {
    mutate(data);
  };

  return (
    <Box>
      <FormikStepper
        initialValues={INITIAL_FORM_STATE}
        onSubmit={(values) => {
          handleSubmit(values);
        }}
        enableReinitialize
        isLoading={isLoading}
      >
        <FormikStep
          validationSchema={FORM_VALIDATION1}
          label="Identification de l'entreprise"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              pt: 2,
            }}
          >
            <Grid
              container
              spacing={2}
              sx={{
                maxWidth: "600px",
              }}
            >
              <Grid item xs={6}>
                <FTextField name="name" label="Nom de l'entreprise *" />
              </Grid>
              <Grid item xs={6}>
                <FTextField name="adresse" label="Adresse de l'entreprise *" />
              </Grid>
              <Grid item xs={12}>
                <FTextField
                  name="num_cnss"
                  label="N° de la CNSS de l'entreprise"
                />
              </Grid>
              <Grid item xs={12}>
                <FTextField
                  name="ICE"
                  type="number"
                  label="ICE de l'entreprise"
                />
              </Grid>
              <Grid item xs={12}>
                <FTextField
                  name="contact_name"
                  label="Nom et prénom du contact"
                />
              </Grid>
              <Grid item xs={12}>
                <FTextField
                  name="contact_email"
                  label="Adresse mail du contact *"
                />
              </Grid>
              <Grid item xs={12}>
                <FTextField
                  name="contact_tel"
                  label="Numéro du telephone du contact"
                />
              </Grid>
            </Grid>
          </Box>
        </FormikStep>

        <FormikStep
          validationSchema={FORM_VALIDATION2}
          label="Identification du besoin"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              pt: 2,
            }}
          >
            <Grid
              container
              spacing={2}
              sx={{
                maxWidth: "600px",
              }}
            >
              <Grid item xs={12}>
                <FFieldArray2 name="activities_products" />
              </Grid>

              <Grid item xs={12}>
                <FFieldArray name="sites" />
              </Grid>

              <Grid item xs={12}>
                <FSelect
                  name="has_cartographie"
                  label="Avez-vous une cartographie des processus ? *"
                  options={["Oui", "Non", "En cours d'établissement"]}
                />
              </Grid>
              <Grid item xs={12}>
                <FSelect
                  name="fournir_cartographie"
                  label="Est-il possible de nous la fournir par mail ? *"
                  options={["Oui", "Non"]}
                  depends
                  dependsOn="has_cartographie"
                  dependsOnValue="Oui"
                />
              </Grid>
              <Grid item xs={12}>
                <FSelect
                  name="has_organigramme"
                  label="Avez-vous un organigramme ? *"
                  options={["Oui", "Non"]}
                />
              </Grid>
              <Grid item xs={12}>
                <FSelect
                  name="fournir_organigramme"
                  label="Est-il possible de nous le fournir par mail ? *"
                  options={["Oui", "Non"]}
                  depends
                  dependsOn="has_organigramme"
                  dependsOnValue="Oui"
                />
              </Grid>
              <Grid item xs={12}>
                <FSelect
                  name="has_procedures"
                  label="Avez-vous des procédures écrites ? *"
                  options={["Oui", "Non"]}
                />
              </Grid>
              <Grid item xs={12}>
                <FTextField
                  name="number_procedures"
                  type="number"
                  label="Combien ? *"
                  depends
                  dependsOn="has_procedures"
                  dependsOnValue="Oui"
                />
              </Grid>
              <Grid item xs={12}>
                <FSelect
                  name="legale_reglementaire"
                  label="Réalisez-vous une veille légale et réglementaire ? (lois QSE applicables à votre activité) *"
                  options={["Oui", "Non"]}
                />
              </Grid>
            </Grid>
          </Box>
        </FormikStep>
        <FormikStep
          validationSchema={FORM_VALIDATION3}
          label="Détails de projet"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              pt: 2,
            }}
          >
            <Grid
              container
              spacing={2}
              sx={{
                maxWidth: "600px",
              }}
            >
              <Grid item xs={12}>
                <FMultiSelect
                  name="demarche"
                  label="Quelle démarche souhaitez-vous ? *"
                  options={[
                    "Qualité : ISO 9001 v2015",
                    "Sécurité : ISO 45001 v2018",
                    "Environnement : ISO 14001 v2015",
                    "Autres",
                  ]}
                />
              </Grid>
              <Grid item xs={12}>
                <FTextField
                  name="other_demarches"
                  label="Autres demarches *"
                  depends
                  dependsOn="demarche"
                  dependsOnValue="Autres"
                />
              </Grid>

              <Grid item xs={12}>
                <FSelect
                  name="intern_charge"
                  label="Une personne en interne sera-t-elle chargée de la mission ? *"
                  options={["Oui", "Non"]}
                />
              </Grid>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid item xs={12}>
                  <FDatePicker
                    name="start_date"
                    label="Date de début souhaitée *"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FDatePicker
                    name="certification_date"
                    label="Date de certification souhaitée *"
                  />
                </Grid>
              </LocalizationProvider>
              <Grid item xs={12}>
                <FSelect
                  name="financement"
                  label="Souhaitez-vous un financement à votre projet ? *"
                  options={["Oui", "Non"]}
                />
              </Grid>

              <Grid item xs={12}>
                <FTextField
                  name="financement_organisme"
                  label="Avez-vous un organisme de préférence ? *"
                  depends
                  dependsOn="financement"
                  dependsOnValue="Oui"
                />
              </Grid>

              <Grid item xs={12}>
                <FSelect
                  name="has_certification_organisme"
                  label="Avez-vous une préférence pour un organisme de certification ? *"
                  options={["Oui", "Non"]}
                />
              </Grid>

              <Grid item xs={12}>
                <FTextField
                  name="certification_organisme"
                  label="lequel ? *"
                  depends
                  dependsOn="has_certification_organisme"
                  dependsOnValue="Oui"
                />
              </Grid>
            </Grid>
          </Box>
        </FormikStep>
      </FormikStepper>
      <Box sx={{ marginTop: "30px" }}>
        <CustomAlert alert={alert} setAlert={setAlert} />
      </Box>
    </Box>
  );
};

export default AfriqualeosForm;
